import { ProcessingEvent } from '@renderforest/webwood-engine'
import { useCallback, useEffect, useRef } from 'react'
import { ISelectionSpan } from '../types'
import { WebWoodEngine } from '@renderforest/webwood-engine'
import { indexFromDomPosition } from '../dom-text'

export const useCursor = (
  setSelectionSpans: React.Dispatch<React.SetStateAction<ISelectionSpan[]>>,
  editorRef: React.RefObject<HTMLDivElement | null>,
  engineId: string,
  itemId: string,
  engine: WebWoodEngine | null
) => {
  const cursorRef = useRef<HTMLDivElement>(null)

  const updateCursor = useCallback(() => {
    const root = editorRef.current
    const cursorEl = cursorRef.current
    if (!engine || !root || !cursorEl) return
    const sel = window.getSelection()

    if (
      !sel ||
      sel.rangeCount === 0 ||
      !sel.focusNode ||
      !root.contains(sel.focusNode) ||
      !sel.anchorNode ||
      !root.contains(sel.anchorNode)
    ) {
      cursorEl.style.visibility = 'hidden'
      setSelectionSpans([])
      return
    }
    const focusIndex = indexFromDomPosition(
      root,
      sel.focusNode,
      sel.focusOffset
    )
    const anchorIndex = indexFromDomPosition(
      root,
      sel.anchorNode,
      sel.anchorOffset
    )

    // Cursor follows the focus end of the selection (where the cursor "is").
    const cursorRect =
      engine.getEditorText(engineId)?.getCursorRect(focusIndex) ?? null
    if (!cursorRect) {
      cursorEl.style.visibility = 'hidden'
      setSelectionSpans([])
      return
    }
    cursorEl.style.visibility = 'visible'
    cursorEl.style.left = `${cursorRect.x}px`
    cursorEl.style.top = `${cursorRect.y}px`
    cursorEl.style.height = `${cursorRect.height}px`

    // Compute selection rectangles using the engine's cached layout. One rect
    // per visible line that the [start, end] range overlaps.
    if (focusIndex === anchorIndex) {
      setSelectionSpans([])
      return
    }
    const layout = engine.getEditorText(engineId)?.getLayout() ?? null
    if (!layout) {
      setSelectionSpans([])
      return
    }
    const start = Math.min(anchorIndex, focusIndex)
    const end = Math.max(anchorIndex, focusIndex)
    const lineHeightPx = layout.ascenderPx - layout.descenderPx
    const spans: ISelectionSpan[] = []
    for (let i = 0; i < layout.lines.length; i++) {
      const line = layout.lines[i]
      if (line.endIndex < start || line.startIndex > end) continue
      const sCol = Math.max(
        0,
        Math.min(start - line.startIndex, line.glyphXs.length - 1)
      )
      const eCol = Math.max(
        0,
        Math.min(end - line.startIndex, line.glyphXs.length - 1)
      )
      if (eCol <= sCol) continue
      spans.push({
        x: line.bitmapOffsetX + line.glyphXs[sCol],
        y: i * layout.lineStep + layout.strokePad,
        width: line.glyphXs[eCol] - line.glyphXs[sCol],
        height: lineHeightPx
      })
    }
    setSelectionSpans(spans)
  }, [engine, engineId, editorRef, setSelectionSpans])

  // Fire on every DOM selection change while the editor owns focus.
  useEffect(() => {
    const handler = () => updateCursor()
    document.addEventListener('selectionchange', handler)
    return () => document.removeEventListener('selectionchange', handler)
  }, [updateCursor])

  // Re-position the cursor whenever the engine emits a layout-affecting event
  // (text changes, font-size changes, side-resize, etc.) — cursor X/Y depends
  // on the just-updated `lastLayout` cached in the Text object.
  useEffect(() => {
    if (!engine) return
    const handler = (data: { id: string }) => {
      if (data.id !== itemId) return
      updateCursor()
    }
    engine.signals.on(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
    engine.signals.on(ProcessingEvent.OBJECT_DIMENSIONS_RESOLVED, handler)
    return () => {
      engine.signals.off(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
      engine.signals.off(ProcessingEvent.OBJECT_DIMENSIONS_RESOLVED, handler)
    }
  }, [engine, itemId, updateCursor])

  // Initial position on mount (selectionchange may have fired before engine
  // hooks were attached).
  useEffect(() => {
    updateCursor()
  }, [updateCursor])

  return { cursorRef }
}
